/**
 * 서비스 다이얼로그 관리 Hook
 */
import { useState, useCallback } from 'react';
import { adminAPI } from '@/services/api';
import { MSAService } from '@/types';

interface ConfirmModalState {
  open: boolean;
  title: string;
  message: string;
  type: 'suspend' | 'resume' | 'delete' | 'reject' | 'visibility' | 'restore' | 'cancel_rejection';
  serviceId: number | null;
  newVisibility?: boolean;
  serviceName?: string;
}

export const useServiceDialogs = (
  onSuccess: (message: string) => void,
  onError: (message: string) => void,
  onRefresh: () => Promise<void>
) => {
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    open: false,
    title: '',
    message: '',
    type: 'suspend',
    serviceId: null,
  });

  const openConfirmModal = useCallback((
    type: 'suspend' | 'resume' | 'delete' | 'reject' | 'visibility' | 'restore' | 'cancel_rejection',
    serviceId: number,
    serviceName: string,
    newVisibility?: boolean
  ) => {
    const modalConfig = {
      suspend: { title: '서비스 일시 중지', message: `"${serviceName}" 서비스를 일시 중지하시겠습니까?\n\n일시 중지된 서비스는 구독 중인 고객사에서 이용할 수 없습니다.` },
      resume: { title: '서비스 재시작', message: `"${serviceName}" 서비스를 재시작하시겠습니까?\n\n재시작 후 구독 중인 고객사에서 다시 이용할 수 있습니다.` },
      delete: { title: '서비스 삭제', message: `"${serviceName}" 서비스를 삭제하시겠습니까?\n\n삭제된 서비스는 같은 URL로 재등록할 수 있습니다.` },
      reject: { title: '서비스 등록 거부', message: `"${serviceName}" 서비스 등록을 거부하시겠습니까?\n\n거부된 서비스는 삭제 후 재등록해야 합니다.` },
      restore: { title: '서비스 삭제 취소', message: `"${serviceName}" 서비스의 삭제를 취소하시겠습니까?\n\n삭제 취소 시 서비스가 등록대기중 상태로 변경되어 다시 승인할 수 있습니다.` },
      cancel_rejection: { title: '서비스 거부 취소', message: `"${serviceName}" 서비스의 거부를 취소하시겠습니까?\n\n거부 취소 시 서비스가 등록대기중 상태로 변경되어 다시 승인할 수 있습니다.` },
      visibility: { 
        title: newVisibility ? '서비스 공개' : '서비스 비공개', 
        message: newVisibility 
          ? `"${serviceName}" 서비스를 공개로 변경하시겠습니까?\n\n공개된 서비스는 고객사에서 검색하고 구독할 수 있습니다.`
          : `"${serviceName}" 서비스를 비공개로 변경하시겠습니까?\n\n비공개 서비스는 고객사에서 검색할 수 없습니다.` 
      },
    };
    
    setConfirmModal({
      open: true,
      title: modalConfig[type].title,
      message: modalConfig[type].message,
      type,
      serviceId,
      newVisibility,
      serviceName,
    });
  }, []);

  const handleClearAll = useCallback(async (filteredServices: MSAService[]) => {
    try {
      onError('');
      onSuccess('');
      setShowClearAllDialog(false);
      
      let successCount = 0;
      let failCount = 0;
      
      for (const service of filteredServices) {
        try {
          await adminAPI.permanentlyDeleteService(service.service_slug);
          successCount++;
        } catch (err) {
          failCount++;
          console.error(`Failed to permanently delete service ${service.service_slug}:`, err);
        }
      }
      
      if (successCount > 0) {
        onSuccess(`✅ ${successCount}개 서비스가 완전히 삭제되었습니다.${failCount > 0 ? ` (${failCount}개 실패)` : ''}`);
        await onRefresh();
      } else {
        onError(`모든 서비스 삭제에 실패했습니다.`);
      }
    } catch (error) {
      onError('서비스 삭제 중 오류가 발생했습니다.');
    }
  }, [onSuccess, onError, onRefresh]);

  return {
    // State
    approveModalOpen,
    showClearAllDialog,
    confirmModal,
    // Actions
    setApproveModalOpen,
    setShowClearAllDialog,
    setConfirmModal,
    openConfirmModal,
    handleClearAll,
  };
};
